/**
 * StatusButton – Action button that reflects step status.
 * Figma-matched: Start (teal), Resume (amber), Review/Edit (green with checkmark).
 */

import { SETTLEMENT_PLANNER_STEP_CARD } from './settlementTheme';

type StepStatus = 'not-started' | 'in-progress' | 'complete';

interface StatusButtonProps {
  status: StepStatus;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  appearance?: 'default' | 'planner';
}

const STATUS_CONFIG: Record<StepStatus, { label: string; bg: string; text: string; hasCheck?: boolean }> = {
  'not-started': {
    label: 'Start',
    bg: 'bg-blue-600 hover:bg-blue-900',
    text: 'text-white-alt',
  },
  'in-progress': {
    label: 'Resume',
    bg: 'bg-orange-100 hover:bg-orange-300 border border-orange-300',
    text: 'text-orange-900',
  },
  complete: {
    label: 'Review/Edit',
    bg: 'bg-green-600 hover:bg-green-900',
    text: 'text-white-alt',
    hasCheck: true,
  },
};

export default function StatusButton({ status, onClick, disabled, className = '', appearance = 'default' }: StatusButtonProps) {
  const config = STATUS_CONFIG[status];
  const plannerLabel =
    status === 'in-progress' ? 'Resume' : status === 'complete' ? 'Review / Edit' : 'Start';

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className} ${
        appearance === 'planner'
          ? SETTLEMENT_PLANNER_STEP_CARD.button
          : `px-5 py-1.5 rounded-md text-sm font-semibold min-h-[32px] ${config.bg} ${config.text}`
      }`}
    >
      {appearance !== 'planner' && config.hasCheck && (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {appearance === 'planner' ? plannerLabel : config.label}
    </button>
  );
}
